import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { refreshAccessToken, refreshAdminAccessToken } from '@/services/apiClient';
import * as authService from '@/services/authService';
import { useI18n } from '@/hooks/useI18n';
import { getAuthArea, type AuthArea } from '@/utils/authArea';
import { OAUTH_CALLBACK_PATH } from '@/utils/constants';
import { resolveLanguagePreference } from '@/utils/languagePreference';

type LangRef = { current: { lang: string; setLang: (lang: string) => void } };

/** Customer restore: refresh, then load the profile and sync its language. */
async function restoreCustomerSession(langRef: LangRef) {
  const { setUser, setBootstrapping, logout } = useAuthStore.getState();
  try {
    await refreshAccessToken();
    const profile = await authService.getUserInfo();
    setUser(profile);
    // Sync the UI language to the user's saved preference if it differs
    // from the language resolved at startup (browser/cache).
    const pref = resolveLanguagePreference(profile.language_preference);
    if (pref && pref !== langRef.current.lang) {
      langRef.current.setLang(pref);
    }
  } catch {
    // No valid customer refresh cookie, or the refresh succeeded but the
    // profile could not be loaded. refreshAccessToken() already flipped
    // isAuthenticated on, so clear it: the header must never show a
    // signed-in state without a customer profile.
    logout();
  } finally {
    setBootstrapping(false);
  }
}

/** Admin restore: the refresh response carries the admin and permissions. */
async function restoreAdminSession() {
  try {
    await refreshAdminAccessToken();
  } catch {
    // No valid admin refresh cookie — remain logged out.
  } finally {
    useAuthStore.getState().setAdminBootstrapping(false);
  }
}

/**
 * Restores sessions after a page load.
 *
 * Because tokens are never persisted to localStorage (req 4.5), a hard
 * reload starts with no access token. The customer and admin sessions each
 * have their own httpOnly refresh cookie, and this component restores only
 * the session for the area being viewed (`/admin/*` = admin, everything else
 * = customer). The other area's session is restored the first time the user
 * navigates into it client-side. Each restore runs once and then flips that
 * area's bootstrapping flag off, so the guards can show a spinner until it
 * finishes.
 *
 * On the OAuth callback route (OAUTH_CALLBACK_PATH) this component does not
 * restore the customer session: OAuthCallbackPage owns that restore there
 * (same refresh + profile path) and flips `isBootstrapping` itself. Running
 * both would race two independent logins, so the page could show an error
 * while this component signs the user in anyway. The page owns it, rather
 * than awaiting this bootstrap, because it must distinguish "no cookie" from
 * a transient error, and this bootstrap swallows all errors.
 */
export function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const { lang, setLang } = useI18n();
  const { pathname } = useLocation();
  const langRef = useRef({ lang, setLang });
  // Areas whose restore has started. A ref (not effect cleanup) dedupes, so
  // React Strict Mode's effect replay can't start a second restore.
  const startedRef = useRef(new Set<AuthArea>());

  useEffect(() => {
    langRef.current = { lang, setLang };
  }, [lang, setLang]);

  useEffect(() => {
    const area = getAuthArea(pathname);
    if (startedRef.current.has(area)) return;
    startedRef.current.add(area);

    // OAuthCallbackPage restores the customer session on its own route (see
    // above). Ignore trailing slashes: React Router also routes
    // `/oauth/callback/` to the page, and running both logins there would
    // bring back the race.
    if (pathname.replace(/\/+$/, '') === OAUTH_CALLBACK_PATH) return;

    if (area === 'admin') {
      void restoreAdminSession();
    } else {
      void restoreCustomerSession(langRef);
    }
  }, [pathname]);

  return <>{children}</>;
}
